/**
 * IndexedDB utility functions for storing and retrieving data
 * Similar to cookies but with larger storage capacity and better performance
 */

// Database configuration
const DB_NAME = 'linqops_db';
const DB_VERSION = 1;
const STORE_NAME = 'user_data';

// Check if we're running in a browser environment
const isBrowser = typeof window !== 'undefined';

/**
 * Opens a connection to the IndexedDB database
 * @returns Promise with the database connection
 */
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    // Check if we're in a browser environment
    if (!isBrowser) {
      reject(new Error('IndexedDB is only available in browser environments'));
      return;
    }
    
    // Check if IndexedDB is available
    if (!window.indexedDB) {
      reject(new Error('IndexedDB is not available in this browser'));
      return;
    }

    const request = window.indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      reject(new Error('Error opening IndexedDB'));
    };

    request.onsuccess = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      resolve(db);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      
      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'key' });
      }
    };
  });
};

/**
 * Sets an item in the IndexedDB store
 * @param key The key to store the data under
 * @param value The value to store
 * @returns Promise that resolves when the data is stored
 */
export const setItem = async (key: string, value: any): Promise<void> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    console.warn('IndexedDB is only available in browser environments');
    return Promise.resolve();
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.put({ key, value, timestamp: Date.now() });
      
      request.onerror = () => {
        reject(new Error(`Error storing data for key: ${key}`));
      };
      
      request.onsuccess = () => {
        resolve();
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB setItem error:', error);
    return Promise.resolve(); // Fail silently in production
  }
};

/**
 * Gets an item from the IndexedDB store
 * @param key The key to retrieve data for
 * @returns Promise with the retrieved value, or null if not found
 */
export const getItem = async <T = any>(key: string): Promise<T | null> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    console.warn('IndexedDB is only available in browser environments');
    return Promise.resolve(null);
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.get(key);
      
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
      
      request.onerror = () => {
        reject(new Error(`Failed to retrieve data for key: ${key}`));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB getItem error:', error);
    return Promise.resolve(null); // Fail silently in production
  }
};

/**
 * Removes an item from the IndexedDB store
 * @param key The key to remove
 * @returns Promise that resolves when the item is removed
 */
export const removeItem = async (key: string): Promise<void> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    console.warn('IndexedDB is only available in browser environments');
    return Promise.resolve();
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.delete(key);
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(new Error(`Failed to remove data for key: ${key}`));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB removeItem error:', error);
    return Promise.resolve(); // Fail silently in production
  }
};

/**
 * Clears all items from the IndexedDB store
 * @returns Promise that resolves when all items are cleared
 */
export const clearAll = async (): Promise<void> => {
  // Check if we're in a browser environment
  if (!isBrowser) {
    return Promise.resolve(); // Fail silently in production
  }
  
  try {
    const db = await openDB();
    
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      
      const request = store.clear();
      
      request.onsuccess = () => {
        resolve();
      };
      
      request.onerror = () => {
        reject(new Error('Failed to clear all data'));
      };
      
      transaction.oncomplete = () => {
        db.close();
      };
    });
  } catch (error) {
    console.error('IndexedDB clearAll error:', error);
    return Promise.resolve(); // Fail silently in production
  }
};

/**
 * Subscription data interface
 */
export interface SubscriptionData {
  subscriptionPlanId: string;
  subscriptionPlanName: string;
  restrictedPages: string[];
}

export interface EmployeeData {
  id: string;
}

/**
 * Interface for the last messages cache
 * Key is employeeId, value is the last message text
 */
export interface LastMessagesData {
  [employeeId: string]: string;
}

/**
 * Sets subscription data in IndexedDB
 * @param data The subscription data to store
 */
export const setSubscriptionData = async (data: SubscriptionData): Promise<void> => {
  return setItem('subscription_data', data);
};

/**
 * Gets subscription data from IndexedDB
 * @returns Promise with the subscription data, or null if not found
 */
export const getSubscriptionData = async (): Promise<SubscriptionData | null> => {
  return getItem<SubscriptionData>('subscription_data');
};

/**
 * Removes subscription data from IndexedDB
 */
export const removeSubscriptionData = async (): Promise<void> => {
  return removeItem('subscription_data');
};

export const setEmployeeData = async (data: EmployeeData): Promise<void> => {
  return setItem('employee_data', data);
};

export const getEmployeeData = async (): Promise<EmployeeData | null> => {
  return getItem<EmployeeData>('employee_data');
};

export const removeEmployeeData = async (): Promise<void> => {
  return removeItem('employee_data');
};

export const setLastDispatchMessagesData = async (data: LastMessagesData): Promise<void> => {
  return setItem('last_dispatch_messages_data', data);
};

export const getLastDispatchMessagesData = async (): Promise<LastMessagesData | null> => {
  return getItem<LastMessagesData>('last_dispatch_messages_data');
};

export const setLastHRMessagesData = async (data: LastMessagesData): Promise<void> => {
  return setItem('last_hr_messages_data', data);
};

export const getLastHRMessagesData = async (): Promise<LastMessagesData | null> => {
  return getItem<LastMessagesData>('last_hr_messages_data');
};